/*
 * Carrossel "Avaliações de quem vive de plantão" — ENN-8.
 *
 * A lógica pura (formatar nota, filtrar, ordenar, remover duplicada, montar o carrossel
 * com fallback) fica isolada no objeto `kitAvaliacoes` abaixo, sem depender de DOM nem de
 * rede — é o que `scripts/kit-avaliacoes.test.mjs` exercita em Node. O resto do arquivo
 * (busca na API do Judge.me, parse do HTML devolvido, o custom element em si) só roda no
 * navegador, atrás de `typeof window !== 'undefined'` — em Node esse bloco nunca executa,
 * então o arquivo pode ser carregado com `new Function(fonte)()` sem stub nenhum.
 *
 * Fontes de dados (ver JULGAMENTOS.md a partir de R53):
 * - `reviews_for_widget` (cdn.judge.me), por produto: `.jdgm-rev[data-review-id]`,
 *   nota em `.jdgm-rev__rating[data-score]`, corpo em `.jdgm-rev__body`, data em
 *   `time.jdgm-rev__timestamp[datetime]` (ISO 8601).
 * - Fallback: bloco de app "Featured carousel" do Judge.me, renderizado oculto na própria
 *   section. `.jdgm-carousel-item[data-review-id]`, nota = nº de `.jdgm-star.jdgm--on`,
 *   corpo em `.jdgm-carousel-item__review-body`, data em
 *   `.jdgm-carousel-item__timestamp[data-time]` (formato MM/DD/AAAA).
 */
(function () {
  'use strict';

  var MINIMO_AVALIACOES = 3;
  var NOTA_MINIMA_PADRAO = 4;

  /** "4.99" / 5 → "5,0". Não numérico → null (quem chama decide o que fazer). */
  function formatarNotaPtBr(nota) {
    var n = Number(nota);
    if (!isFinite(n)) return null;
    var limitada = Math.min(5, Math.max(0, n));
    return limitada.toFixed(1).replace('.', ',');
  }

  /** Quantas das 5 estrelas saem cheias — nota arredondada e limitada a [0, 5]. */
  function estrelasPreenchidas(nota) {
    var n = Number(nota);
    if (!isFinite(n)) return 0;
    return Math.round(Math.min(5, Math.max(0, n)));
  }

  /** "09/20/2026" (MM/DD/AAAA, formato do widget de carrossel do Judge.me) → epoch ms (UTC). */
  function parseDataAmericana(texto) {
    if (typeof texto !== 'string') return NaN;
    var m = texto.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!m) return NaN;
    var mes = Number(m[1]);
    var dia = Number(m[2]);
    var ano = Number(m[3]);
    return Date.UTC(ano, mes - 1, dia);
  }

  /**
   * Só passam avaliações com nota ≥ notaMinima (padrão 4) e corpo não vazio — espelha o
   * próprio carrossel em destaque do Judge.me (ver R54). Não muta o array recebido.
   */
  function filtrarReviewsValidas(reviews, opcoes) {
    var notaMinima = (opcoes && opcoes.notaMinima) || NOTA_MINIMA_PADRAO;
    return (reviews || []).filter(function (r) {
      if (!r) return false;
      var nota = Number(r.nota);
      var corpo = typeof r.corpo === 'string' ? r.corpo.trim() : '';
      return isFinite(nota) && nota >= notaMinima && corpo.length > 0;
    });
  }

  /**
   * Mais recente primeiro. Estável: duas avaliações com a mesma data (ou ambas sem data
   * reconhecida) mantêm a ordem relativa de entrada; sem-data vai para o fim.
   */
  function ordenarPorDataDesc(reviews) {
    return (reviews || [])
      .map(function (r, indice) {
        return { r: r, indice: indice };
      })
      .sort(function (a, b) {
        var ta = isFinite(a.r.dataMs) ? a.r.dataMs : -Infinity;
        var tb = isFinite(b.r.dataMs) ? b.r.dataMs : -Infinity;
        if (ta !== tb) return tb - ta;
        return a.indice - b.indice;
      })
      .map(function (item) {
        return item.r;
      });
  }

  /** Remove avaliações repetidas (mesmo `id`), mantendo a primeira ocorrência. */
  function removerDuplicadas(reviews) {
    var vistos = Object.create(null);
    var resultado = [];
    (reviews || []).forEach(function (r) {
      var id = r && r.id;
      if (id == null || vistos[id]) return;
      vistos[id] = true;
      resultado.push(r);
    });
    return resultado;
  }

  /**
   * Pipeline completo: filtra + ordena as avaliações dos produtos; se sobrarem menos que
   * `minimo`, completa com as avaliações gerais da loja (também filtradas e ordenadas),
   * sem duplicar por `id`. As avaliações dos produtos vêm primeiro (mais relevantes ao tema da
   * section) — a geral só entra para completar a cota, nunca empurra uma de produto para
   * fora (ver R59). Corta em `limite` no final.
   */
  function montarCarrossel(opcoes) {
    var reviewsProdutos = (opcoes && opcoes.reviewsProdutos) || [];
    var geral = (opcoes && opcoes.geral) || [];
    var minimo = (opcoes && opcoes.minimo) || MINIMO_AVALIACOES;
    var limite = (opcoes && opcoes.limite) || 10;
    var notaMinima = (opcoes && opcoes.notaMinima) || NOTA_MINIMA_PADRAO;

    var validasProdutos = ordenarPorDataDesc(filtrarReviewsValidas(reviewsProdutos, { notaMinima: notaMinima }));
    var combinadas = validasProdutos;

    if (validasProdutos.length < minimo) {
      var validasGeral = ordenarPorDataDesc(filtrarReviewsValidas(geral, { notaMinima: notaMinima }));
      combinadas = validasProdutos.concat(validasGeral);
    }

    return removerDuplicadas(combinadas).slice(0, limite);
  }

  /**
   * Posições de rolagem que o trilho alcança, uma por bolinha. `offsets` é o início de cada
   * card relativo ao primeiro; `maxScroll` é scrollWidth − clientWidth. O trilho não passa
   * de `maxScroll`, então os últimos cards (já visíveis) não ganham posição própria: com 10
   * cards e 3 à vista são 8 posições, e a última é o fim do trilho.
   */
  function posicoesDeRolagem(offsets, maxScroll) {
    var limite = Math.max(0, maxScroll || 0);
    var posicoes = [0];
    for (var i = 1; i < offsets.length; i += 1) {
      var x = Math.min(offsets[i], limite);
      if (x - posicoes[posicoes.length - 1] > 1) posicoes.push(x);
      if (x >= limite) break;
    }
    return posicoes;
  }

  function indiceMaisProximo(posicoes, scrollLeft) {
    var maisProximo = 0;
    var menorDistancia = Infinity;
    for (var i = 0; i < posicoes.length; i += 1) {
      var distancia = Math.abs(posicoes[i] - scrollLeft);
      if (distancia < menorDistancia) {
        menorDistancia = distancia;
        maisProximo = i;
      }
    }
    return maisProximo;
  }

  var kitAvaliacoes = {
    formatarNotaPtBr: formatarNotaPtBr,
    estrelasPreenchidas: estrelasPreenchidas,
    parseDataAmericana: parseDataAmericana,
    filtrarReviewsValidas: filtrarReviewsValidas,
    ordenarPorDataDesc: ordenarPorDataDesc,
    removerDuplicadas: removerDuplicadas,
    montarCarrossel: montarCarrossel,
    posicoesDeRolagem: posicoesDeRolagem,
    indiceMaisProximo: indiceMaisProximo,
  };

  if (typeof window === 'undefined') {
    // Node (testes): sem DOM, sem customElements. Só expõe a lógica pura.
    if (typeof module !== 'undefined' && module.exports) {
      module.exports = kitAvaliacoes;
    } else if (typeof globalThis !== 'undefined') {
      globalThis.__kitAvaliacoes = kitAvaliacoes;
    }
    return;
  }

  window.FoxTheme = window.FoxTheme || {};
  window.FoxTheme.kitAvaliacoes = kitAvaliacoes;

  if (window.customElements.get('kit-avaliacoes-carrossel')) return;

  /** `.jdgm-rev` (reviews_for_widget) → objeto plano. */
  function extrairReviewsDoWidget(doc) {
    var nos = doc.querySelectorAll('.jdgm-rev');
    return Array.prototype.map.call(nos, function (el) {
      var ratingEl = el.querySelector('.jdgm-rev__rating');
      var bodyEl = el.querySelector('.jdgm-rev__body');
      var timeEl = el.querySelector('time.jdgm-rev__timestamp');
      var dataISO = timeEl ? timeEl.getAttribute('datetime') : null;
      return {
        id: el.getAttribute('data-review-id'),
        produtoTitulo: el.getAttribute('data-product-title') || null,
        nota: ratingEl ? Number(ratingEl.getAttribute('data-score')) : NaN,
        corpo: bodyEl ? bodyEl.textContent.trim() : '',
        dataMs: dataISO ? Date.parse(dataISO) : NaN,
      };
    });
  }

  /** `.jdgm-carousel-item` (Featured carousel, fallback oculto) → objeto plano. */
  function extrairReviewsDoFallback(container) {
    var nos = container.querySelectorAll('.jdgm-carousel-item');
    return Array.prototype.map.call(nos, function (el) {
      var ratingEl = el.querySelector('.jdgm-carousel-item__review-rating');
      var bodyEl = el.querySelector('.jdgm-carousel-item__review-body');
      var timeEl = el.querySelector('.jdgm-carousel-item__timestamp');
      var produtoImg = el.querySelector('.jdgm-carousel-item__product-image');
      var estrelasCheias = ratingEl ? ratingEl.querySelectorAll('.jdgm-star.jdgm--on').length : 0;
      return {
        id: el.getAttribute('data-review-id'),
        produtoTitulo: produtoImg ? produtoImg.getAttribute('alt') : null,
        nota: estrelasCheias,
        corpo: bodyEl ? bodyEl.textContent.trim() : '',
        dataMs: timeEl ? parseDataAmericana(timeEl.getAttribute('data-time')) : NaN,
      };
    });
  }

  class KitAvaliacoesCarrossel extends HTMLElement {
    connectedCallback() {
      this.dominio = this.dataset.lojaDominio || '';
      this.produtoIds = (this.dataset.produtoIds || '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);
      this.porProduto = parseInt(this.dataset.porProduto, 10) || 5;
      this.limite = parseInt(this.dataset.limite, 10) || 10;

      const textosEl = this.querySelector('[data-avaliacoes-textos]');
      try {
        this.textos = textosEl ? JSON.parse(textosEl.textContent) : {};
      } catch (erro) {
        this.textos = {};
      }

      this.faixa = this.querySelector('[data-avaliacoes-faixa]');
      this.trilho = this.querySelector('[data-avaliacoes-trilho]');
      this.bolinhas = this.querySelector('[data-avaliacoes-bolinhas]');
      this.fallback = this.querySelector('[data-avaliacoes-fallback]');

      const anterior = this.querySelector('[data-avaliacoes-anterior]');
      const proxima = this.querySelector('[data-avaliacoes-proxima]');
      if (anterior) anterior.addEventListener('click', () => this.irParaAdjacente(-1));
      if (proxima) proxima.addEventListener('click', () => this.irParaAdjacente(1));
      if (this.trilho) {
        this.trilho.addEventListener('scroll', () => this.atualizarBolinhaAtiva(), { passive: true });
        // A quantidade de posições depende de quantos cards cabem na tela.
        window.addEventListener('resize', () => this.renderizarBolinhas(), { passive: true });
      }

      if (!this.produtoIds.length) {
        this.carregarSoFallback();
        return;
      }

      if (!('IntersectionObserver' in window)) {
        this.carregar();
        return;
      }

      this.observador = new IntersectionObserver(
        (entradas) => {
          entradas.forEach((entrada) => {
            if (entrada.isIntersecting) {
              this.observador.disconnect();
              this.carregar();
            }
          });
        },
        { rootMargin: '200px 0px 200px 0px' }
      );
      this.observador.observe(this);
    }

    buscarReviewsDoProduto(produtoId) {
      const url =
        'https://cdn.judge.me/reviews/reviews_for_widget' +
        '?url=' + encodeURIComponent(this.dominio) +
        '&shop_domain=' + encodeURIComponent(this.dominio) +
        '&platform=shopify' +
        '&per_page=' + this.porProduto +
        '&product_id=' + encodeURIComponent(produtoId);

      return fetch(url)
        .then((res) => {
          if (!res.ok) throw new Error('judge.me respondeu ' + res.status);
          return res.json();
        })
        .then((json) => {
          if (!json || typeof json.html !== 'string') return [];
          const doc = new DOMParser().parseFromString(json.html, 'text/html');
          return extrairReviewsDoWidget(doc);
        })
        .catch(() => []);
    }

    reviewsDoFallback() {
      if (!this.fallback) return [];
      try {
        return extrairReviewsDoFallback(this.fallback);
      } catch (erro) {
        return [];
      }
    }

    carregarSoFallback() {
      Promise.resolve()
        .then(() => {
          const lista = kitAvaliacoes.montarCarrossel({
            reviewsProdutos: [],
            geral: this.reviewsDoFallback(),
            limite: this.limite,
          });
          this.finalizar(lista);
        })
        .catch(() => {
          /* mantém a faixa oculta — a section não quebra por falha aqui */
        });
    }

    carregar() {
      Promise.all(this.produtoIds.map((id) => this.buscarReviewsDoProduto(id)))
        .then((listas) => {
          let reviewsProdutos = [];
          listas.forEach((lista) => {
            reviewsProdutos = reviewsProdutos.concat(lista);
          });
          const lista = kitAvaliacoes.montarCarrossel({
            reviewsProdutos: reviewsProdutos,
            geral: this.reviewsDoFallback(),
            limite: this.limite,
          });
          this.finalizar(lista);
        })
        .catch(() => {
          /* erro de rede: a faixa continua oculta, selo + título seguem visíveis */
        });
    }

    finalizar(lista) {
      if (!lista.length) {
        const section = this.closest('.kit-avaliacoes');
        if (section) section.hidden = true;
        return;
      }
      this.renderizarCards(lista);
      if (this.faixa) this.faixa.hidden = false;
      // Depois de mostrar a faixa: com ela oculta, o trilho não tem largura para medir.
      this.renderizarBolinhas();
    }

    renderizarCards(lista) {
      if (!this.trilho) return;
      this.trilho.textContent = '';
      lista.forEach((review) => {
        this.trilho.appendChild(this.construirCard(review));
      });
    }

    construirCard(review) {
      const textos = this.textos || {};
      const card = document.createElement('article');
      card.className = 'kit-avaliacoes__card';
      card.setAttribute('role', 'listitem');

      const estrelasWrap = document.createElement('div');
      estrelasWrap.className = 'kit-avaliacoes__card-estrelas';
      const cheias = kitAvaliacoes.estrelasPreenchidas(review.nota);
      const notaPtBr = kitAvaliacoes.formatarNotaPtBr(review.nota);
      if (notaPtBr && textos.ariaNota) {
        estrelasWrap.setAttribute('role', 'img');
        estrelasWrap.setAttribute('aria-label', textos.ariaNota.replace('{nota}', notaPtBr));
      }
      for (let i = 0; i < 5; i += 1) {
        estrelasWrap.appendChild(this.construirEstrela(i < cheias));
      }
      card.appendChild(estrelasWrap);

      const citacao = document.createElement('p');
      citacao.className = 'kit-avaliacoes__card-citacao';
      citacao.textContent = '“' + review.corpo + '”';
      card.appendChild(citacao);

      if (review.produtoTitulo) {
        const divisor = document.createElement('hr');
        divisor.className = 'kit-avaliacoes__card-divisor';
        card.appendChild(divisor);

        const produto = document.createElement('p');
        produto.className = 'kit-avaliacoes__card-produto';
        produto.textContent = textos.sobreProduto
          ? textos.sobreProduto.replace('{produto}', review.produtoTitulo)
          : review.produtoTitulo;
        card.appendChild(produto);
      }

      return card;
    }

    construirEstrela(preenchida) {
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('viewBox', '0 0 14.0922 13.4519');
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('focusable', 'false');
      svg.setAttribute(
        'class',
        'kit-avaliacoes__card-estrela' + (preenchida ? '' : ' kit-avaliacoes__card-estrela--vazia')
      );
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('fill', 'currentColor');
      path.setAttribute(
        'd',
        'M6.57057 0.345491C6.72025 -0.115164 7.37195 -0.115164 7.52163 0.345492L8.84221 4.40983C8.90915 4.61584 9.10112 4.75532 9.31774 4.75532L13.5912 4.75532C14.0756 4.75532 14.277 5.37513 13.8851 5.65983L10.4278 8.17173C10.2526 8.29905 10.1792 8.52474 10.2462 8.73075L11.5667 12.7951C11.7164 13.2557 11.1892 13.6388 10.7973 13.3541L7.33999 10.8422C7.16475 10.7149 6.92745 10.7149 6.7522 10.8422L3.29487 13.3541C2.90301 13.6388 2.37578 13.2557 2.52545 12.7951L3.84603 8.73075C3.91297 8.52474 3.83964 8.29905 3.6644 8.17173L0.207066 5.65983C-0.184791 5.37513 0.0165968 4.75532 0.500958 4.75532L4.77446 4.75532C4.99107 4.75532 5.18305 4.61584 5.24999 4.40983L6.57057 0.345491Z'
      );
      svg.appendChild(path);
      return svg;
    }

    renderizarBolinhas() {
      if (!this.bolinhas) return;
      const quantidade = this.posicoes().length;
      // Na rolagem da janela o resize dispara à toa (barra do navegador no celular): só
      // refaz as bolinhas se a quantidade mudou.
      if (this.bolinhas.children.length === quantidade) {
        this.atualizarBolinhaAtiva();
        return;
      }
      this.bolinhas.textContent = '';
      const textos = this.textos || {};
      for (let indice = 0; indice < quantidade; indice += 1) {
        const botao = document.createElement('button');
        botao.type = 'button';
        botao.className = 'kit-avaliacoes__bolinha';
        botao.setAttribute('role', 'tab');
        botao.setAttribute('aria-current', 'false');
        if (textos.irPara) {
          botao.setAttribute('aria-label', textos.irPara.replace('{numero}', String(indice + 1)));
        }
        botao.addEventListener('click', () => this.irParaIndice(indice));
        this.bolinhas.appendChild(botao);
      }
      this.bolinhas.hidden = quantidade <= 1;
      this.atualizarBolinhaAtiva();
    }

    cards() {
      return this.trilho ? Array.prototype.slice.call(this.trilho.children) : [];
    }

    /* Offsets medidos a partir do primeiro card: o `offsetLeft` é relativo ao ancestral
       posicionado, não ao trilho, e usá-lo cru deslocava a rolagem (card da esquerda
       cortado). */
    posicoes() {
      const cards = this.cards();
      if (!this.trilho || !cards.length) return [0];
      const base = cards[0].offsetLeft;
      const offsets = cards.map((card) => card.offsetLeft - base);
      return kitAvaliacoes.posicoesDeRolagem(offsets, this.trilho.scrollWidth - this.trilho.clientWidth);
    }

    irParaIndice(indice) {
      const posicoes = this.posicoes();
      if (!this.trilho || posicoes[indice] === undefined) return;
      this.trilho.scrollTo({ left: posicoes[indice], behavior: 'smooth' });
    }

    irParaAdjacente(delta) {
      const ultima = this.posicoes().length - 1;
      this.irParaIndice(Math.max(0, Math.min(ultima, this.indiceAtivo() + delta)));
    }

    indiceAtivo() {
      if (!this.trilho) return 0;
      return kitAvaliacoes.indiceMaisProximo(this.posicoes(), this.trilho.scrollLeft);
    }

    atualizarBolinhaAtiva() {
      if (!this.bolinhas) return;
      const ativo = this.indiceAtivo();
      Array.prototype.forEach.call(this.bolinhas.children, (botao, indice) => {
        botao.setAttribute('aria-current', indice === ativo ? 'true' : 'false');
      });
    }
  }

  window.customElements.define('kit-avaliacoes-carrossel', KitAvaliacoesCarrossel);
})();
